import { useCallback, useEffect, useState } from "react";
import MoreAboutYouFooter from "../components/signup/MoreAboutYouFooter";
import GeneralInformationFooter from "../components/signup/GeneralInformationFooter";
import GeneralInformation from "../components/signup/GeneralInformation";
import MoreAboutYou from "../components/signup/MoreAboutYou";
import { useParams } from "react-router-dom";
import { storeLpcLoginUrl } from "../common/lpc";
import { notification } from "antd";
import {
  checkEmail,
  checkFirstName,
  checkLastName,
  checkPassword,
  checkConfirmedPassword,
} from "../common/field";
import OnboardingSteps from "../components/signup/OnboardingSteps";
import { changePageTitle } from "../common/page";
import { track } from "../utils";
import Header from "../components/signup/Header";
import Footer from "../components/signup/Footer";
import { trackUserPreference } from "../common/full-story";
import Tab, { TabState } from "../components/signup/Tab";
import ExitForm from "../components/signup/ExitForm";
import alert from "../common/alert";

const formExitWaitingActivationDelay =
  process.env.REACT_APP_FORM_EXIT_WAITING_ACTIVATION_DELAY;

if (!formExitWaitingActivationDelay) {
  throw new Error("Form exit waiting activation delay not defined");
}

function isMoreAboutInformationOk({ countryCode, categoryId, retailerIds }) {
  return (
    countryCode &&
    Number.isInteger(categoryId) &&
    retailerIds &&
    retailerIds.length > 1
  );
}

function isGeneralInformationOk({
  email,
  firstName,
  lastName,
  password,
  confirmedPassword,
  captchaUnblockingCode,
}) {
  return (
    checkEmail(email).isOk &&
    checkFirstName(firstName).isOk &&
    checkLastName(lastName).isOk &&
    checkPassword(password).isOk &&
    checkConfirmedPassword(password, confirmedPassword).isOk &&
    captchaUnblockingCode
  );
}

export default function Signup() {
  const [api, contextHolder] = notification.useNotification();
  const { lpcLoginUrl } = useParams();
  const [generalInformationState, setGeneralInformationState] = useState(
    TabState.DISABLED
  );
  const [generalInformationData, setGeneralInformationData] = useState({
    retailersForSelectedCountry: [],
  });
  const [generalInformationDataOk, setGeneralInformationDataOk] =
    useState(false);
  const [generalInformationTooltip, setGeneralInformationTooltip] =
    useState(null);

  const [showGeneralInformation, setShowGeneralInformation] = useState(false);

  const [moreAboutYouState, setMoreAboutYouState] = useState(TabState.DISABLED);
  const [moreAboutYouData, setMoreAboutYouData] = useState({});
  const [moreAboutYouDataOk, setMoreAboutYouDataOk] = useState(false);
  const [disableMoreAboutYou, setDisabledMoreAboutYou] = useState(false);
  const [showMoreAboutYou, setShowMoreAboutYou] = useState(true);

  const [resetCaptcha, setResectCaptcha] = useState(0);
  const [openExitForm, setOpenExitForm] = useState(false);
  const [referenceExitFormTime, setReferenceExitFormTime] = useState(
    new Date()
  );
  const [activeExitForm, setActiveExitForm] = useState(true);

  useEffect(() => {
    const exitFormInterval = setInterval(() => {
      if (
        activeExitForm &&
        new Date().getTime() - referenceExitFormTime.getTime() >=
          formExitWaitingActivationDelay
      ) {
        setActiveExitForm(false);
        setOpenExitForm(true);
      }
    }, 1000);

    return () => clearInterval(exitFormInterval);
  }, [referenceExitFormTime, activeExitForm]);

  useEffect(() => {
    if (lpcLoginUrl && storeLpcLoginUrl(lpcLoginUrl)) {
      window.location.href = "/#signup";
    }
  }, [lpcLoginUrl]);

  useEffect(() => {
    const moreAboutYouDateOk = isMoreAboutInformationOk(moreAboutYouData);
    setMoreAboutYouDataOk(moreAboutYouDateOk);

    if (showMoreAboutYou) {
      setMoreAboutYouState(TabState.SELECTED);

      if (moreAboutYouDateOk) {
        setGeneralInformationState(TabState.UNSELECTED);
        setGeneralInformationTooltip(null);
      } else {
        setGeneralInformationState(TabState.DISABLED);
        setGeneralInformationTooltip("Please configure the tracker");
      }
    }
  }, [showMoreAboutYou, moreAboutYouData, setMoreAboutYouDataOk]);

  useEffect(
    () =>
      setGeneralInformationDataOk(
        isGeneralInformationOk(generalInformationData)
      ),
    [generalInformationData]
  );

  useEffect(() => {
    if (showGeneralInformation) {
      setMoreAboutYouState(TabState.UNSELECTED);
      setGeneralInformationState(TabState.SELECTED);
      setGeneralInformationTooltip(null);
    }
  }, [showGeneralInformation]);

  useEffect(() => {
    track("Sign up form opened");
    changePageTitle("Sign up for Live Price Check");
  }, []);

  const onGeneralInformationData = useCallback(
    (data) => setGeneralInformationData(data),
    []
  );

  const onNextButtonClicked = useCallback(() => {
    trackUserPreference(
      moreAboutYouData?.countryName,
      moreAboutYouData?.categoryName,
      moreAboutYouData?.retailersDomains
    );

    setShowMoreAboutYou(false);
    setShowGeneralInformation(true);
    track("Next button clicked on signup form retailer selection page");
  }, [moreAboutYouData]);

  const onMoreAboutYouData = useCallback((data) => {
    setMoreAboutYouData(data);
  }, []);

  const onGoBackButtonClicked = useCallback(() => {
    setShowMoreAboutYou(true);
    setShowGeneralInformation(false);
  }, []);
  const onBeginCreatingAccount = useCallback(
    () => setDisabledMoreAboutYou(true),
    []
  );
  const onEndCreatingAccount = useCallback((errorOccurred) => {
    setDisabledMoreAboutYou(false);
    if (errorOccurred) {
      setResectCaptcha((resetCaptcha) => resetCaptcha + 1);
      setGeneralInformationData({});
    }
  }, []);

  const onSelectMoreAboutYouTab = useCallback(
    () => onGoBackButtonClicked(),
    [onGoBackButtonClicked]
  );

  const onSelectGeneralInformation = useCallback(
    () => onNextButtonClicked(),
    [onNextButtonClicked]
  );

  const onMessageSentFromExitForm = useCallback(() => {
    setOpenExitForm(false);
    setActiveExitForm(false);
    alert.success(api, "Your message is sent. Thank you!");
  }, [api]);

  const resetFormExitReferenceTime = useCallback(
    () => setReferenceExitFormTime(new Date()),
    []
  );

  const onCloseExitForm = useCallback(() => {
    setReferenceExitFormTime(new Date());
    setActiveExitForm(true);
    setOpenExitForm(false);
  }, []);

  return (
    <div className="bg-gray-100 min-w-[1000px]">
      {contextHolder}
      <Header />
      <div className="h-screen flex items-center justify-center min-h-[850px] min-w-[1000px]">
        <div className="flex">
          <OnboardingSteps current={1} />
          <div className="flex flex-col justify-between bg-white rounded-r-lg">
            <div className="px-[30px] py-8 w-[600px]">
              <div className="text-black font-semibold text-[24px] mb-6 leading-[34px]">
                Let's personalize your price tracker
              </div>
              <div className="flex gap-x-6">
                <Tab
                  tabState={moreAboutYouState}
                  areFieldsValid={moreAboutYouDataOk}
                  onSelect={onSelectMoreAboutYouTab}
                  onActivity={resetFormExitReferenceTime}
                >
                  1. Configure the tracker
                </Tab>
                <Tab
                  tabState={generalInformationState}
                  areFieldsValid={generalInformationDataOk}
                  tooltip={generalInformationTooltip}
                  onSelect={onSelectGeneralInformation}
                  onActivity={resetFormExitReferenceTime}
                >
                  2. Save your profile
                </Tab>
              </div>
              <MoreAboutYou
                disabled={disableMoreAboutYou}
                visible={showMoreAboutYou}
                onData={onMoreAboutYouData}
                onActivity={resetFormExitReferenceTime}
              />
              <GeneralInformation
                onData={onGeneralInformationData}
                visible={showGeneralInformation}
                resetCaptcha={resetCaptcha}
                onActivity={resetFormExitReferenceTime}
              />
            </div>
            {showMoreAboutYou ? (
              <MoreAboutYouFooter
                disabled={!moreAboutYouDataOk}
                onNextButtonClicked={onNextButtonClicked}
                onActivity={resetFormExitReferenceTime}
              />
            ) : null}
            {showGeneralInformation ? (
              <GeneralInformationFooter
                generalInformationData={generalInformationData}
                moreAboutYouData={moreAboutYouData}
                disabled={!generalInformationDataOk}
                onBeginCreatingAccount={onBeginCreatingAccount}
                onEndCreatingAccount={onEndCreatingAccount}
                onActivity={resetFormExitReferenceTime}
              />
            ) : null}
          </div>
        </div>
        <Footer />
      </div>
      <ExitForm
        open={openExitForm}
        onMessageSent={onMessageSentFromExitForm}
        onClose={onCloseExitForm}
      />
    </div>
  );
}
